import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { collection, query, where, orderBy, limit, onSnapshot, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { toast } from 'react-toastify';

function resolveSubeKeyFromStorage() {
  try {
    const raw = localStorage.getItem('aktifPersonel');
    if (!raw) return '';
    const personel = JSON.parse(raw);
    const list = personel?.yetkiler?.subeler;
    if (Array.isArray(list) && list.length > 0) return String(list[0]).trim();
  } catch (_) {
    /* ignore */
  }
  return '';
}

// Günsonu kaydedilmediyse bu saatlerde uyarı çıkar; kayıt varsa hiç çıkmaz
const EOD_REMINDER_TIMES = ['19:00', '19:15', '19:45', '20:00'];
const EOD_AUTO_CLOSE_MS = 10_000;

const Notification = () => {
  const location = useLocation();
  const querySube = new URLSearchParams(location.search).get('sube');
  const subeKey =
    (querySube && querySube.trim() && querySube !== 'Bilinmeyen Şube' ? querySube.trim() : '') ||
    resolveSubeKeyFromStorage();

  // --- 1. ESKİ ÖZELLİK (GÜN SONU UYARISI) ---
  const [showEodModal, setShowEodModal] = useState(false);
  /** Firestore’da bugün için günsonu kasa dokümanı var mı (gunsonukasa.js ile aynı doc id: ISO günü) */
  const [eodSavedToday, setEodSavedToday] = useState(false);
  /** İlk okuma gelmeden "kaydedilmemiş" sanıp uyarı göstermemek için */
  const [eodStatusKnown, setEodStatusKnown] = useState(false);
  /** Her uyarı saati günde bir kez tetiklenir (ör. "2026-09-16 19:00") */
  const firedSlotsRef = useRef(new Set());

  // --- 2. YENİ ÖZELLİK İÇİN REFLER ---
  const isFirstRun = useRef(true);
  const activeToastId = useRef(null); 
  const notificationCount = useRef(0);
  
  // YENİ EKLEME: Sayfa açılış zamanını kaydet
  const loadTime = useRef(new Date()); 

  // ========================================================
  // GÖREV A: FIREBASE BİLDİRİMLERİNİ DİNLE (KIRMIZI SAYAÇLI + ZAMAN KONTROLLÜ)
  // ========================================================
  useEffect(() => {
    // Kurallar şube hesabına yalnız kendi şubesinin ve "hepsi" bildirimlerini
    // okutur; filtresiz sorgu tümden reddediliyordu (permission-denied).
    if (!subeKey) return undefined;
    isFirstRun.current = true;
    const q = query(
      collection(db, 'bildirimler'),
      where('hedefSube', 'in', [subeKey, 'hepsi']),
      orderBy('tarih', 'desc'),
      limit(1)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (isFirstRun.current) {
        isFirstRun.current = false;
        return;
      }

      snapshot.docChanges().forEach((change) => {
        // Sadece eklenenleri dikkate al
        if (change.type === "added") {
          const data = change.doc.data();
          
          // --- KRİTİK DÜZELTME: ZAMAN KONTROLÜ ---
          // Mesajın tarihini al (Firebase Timestamp veya Date olabilir)
          let msgDate = null;
          if (data.tarih && data.tarih.toDate) {
             msgDate = data.tarih.toDate(); // Firebase Timestamp ise
          } else if (data.tarih) {
             msgDate = new Date(data.tarih); // Normal tarih ise
          }

          // Eğer mesajın tarihi, sayfa açılışından (loadTime) daha eskiyse
          // bu "silme işleminden dolayı yukarı kayan eski bir mesajdır".
          // Bunu görmezden gel ve çık.
          if (msgDate && msgDate < loadTime.current) {
             return; 
          }
          // ----------------------------------------

          // Buraya geldiyse bu gerçekten YENİ bir mesajdır.
          notificationCount.current += 1;
          const count = notificationCount.current;

          const messageText = `🔔 YENİ BİLDİRİM ${count > 1 ? `(${count})` : ''}`;

          if (activeToastId.current && toast.isActive(activeToastId.current)) {
            toast.update(activeToastId.current, {
              render: messageText,
            });
          } else {
            activeToastId.current = toast.error(messageText, {
              position: "top-right",
              autoClose: false,       
              hideProgressBar: true,  
              closeOnClick: true,     
              pauseOnHover: true,
              draggable: false,
              theme: "colored",       
              style: { 
                backgroundColor: '#d32f2f', 
                color: 'white', 
                fontWeight: 'bold',
                fontSize: '16px',
                textAlign: 'center'
              },
              onClose: () => { 
                notificationCount.current = 0; 
                activeToastId.current = null;
                // Bildirim kapatıldığında "loadTime"ı güncelle ki 
                // o anki mesajı tekrar eski sanmasın (opsiyonel güvenlik)
                loadTime.current = new Date(); 
              } 
            });
          }
        }
      });
    });

    return () => unsubscribe();
  }, [subeKey]);

  // Bugünün günsonu kasa kaydı (şube + takvim günü); kayıt gelince kalan slot uyarıları gelmez
  useEffect(() => {
    if (!subeKey) {
      setEodSavedToday(false);
      return undefined;
    }

    let unsubDoc = () => {};
    const subscribedDayRef = { current: null };

    const attachTodayDoc = () => {
      const today = new Date().toISOString().split('T')[0];
      if (subscribedDayRef.current === today) return;
      subscribedDayRef.current = today;
      unsubDoc();
      const docRef = doc(db, 'subeler', subeKey, 'gunsonukasa', today);
      setEodStatusKnown(false);
      unsubDoc = onSnapshot(
        docRef,
        (snap) => {
          setEodSavedToday(snap.exists());
          setEodStatusKnown(true);
        },
        (err) => {
          // Okunamıyorsa (ör. oturum henüz açılmadı) uyarı yine gösterilsin; bir dakika sonra tekrar dene
          console.warn('Günsonu durumu okunamadı:', err?.code || err);
          setEodSavedToday(false);
          setEodStatusKnown(true);
          subscribedDayRef.current = null;
        }
      );
    };

    attachTodayDoc();
    const dayTicker = setInterval(attachTodayDoc, 60_000);

    return () => {
      clearInterval(dayTicker);
      unsubDoc();
    };
  }, [subeKey]);

  useEffect(() => {
    if (eodSavedToday) setShowEodModal(false);
  }, [eodSavedToday]);

  // ========================================================
  // GÖREV B: SAAT KONTROLÜ (GÜN SONU MODALI)
  // ========================================================
  useEffect(() => {
    if (eodSavedToday || !eodStatusKnown) return undefined;

    const checkTime = () => {
      const now = new Date();
      const hhmm = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      if (!EOD_REMINDER_TIMES.includes(hhmm)) return;
      const dayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const slotKey = `${dayKey} ${hhmm}`;
      // Kontrol 10 sn'de bir çalışıyor; aynı dakika içinde kapanan uyarı yeniden açılmasın
      if (firedSlotsRef.current.has(slotKey)) return;
      firedSlotsRef.current.add(slotKey);
      setShowEodModal(true);
    };

    checkTime();
    const interval = setInterval(checkTime, 10000);
    return () => clearInterval(interval);
  }, [eodSavedToday, eodStatusKnown]);

  // Uyarı ekrana geldikten 10 saniye sonra kendiliğinden kapanır
  useEffect(() => {
    if (!showEodModal) return undefined;
    const timer = setTimeout(() => setShowEodModal(false), EOD_AUTO_CLOSE_MS);
    return () => clearTimeout(timer);
  }, [showEodModal]);

  if (!showEodModal) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: '#fff',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
        zIndex: 10000,
        textAlign: 'center',
        border: '2px solid red'
      }}
    >
      <p style={{ fontSize: '24px', marginBottom: '10px', color: '#000' }}>
        Lütfen günsonu kasa sayımını yapınız
      </p>
      <div style={{ height: '4px', background: '#fde2e2', borderRadius: '2px', overflow: 'hidden', margin: '0 0 14px' }}>
        <div
          style={{
            height: '100%',
            background: '#d32f2f',
            animation: `eodCountdown ${EOD_AUTO_CLOSE_MS}ms linear forwards`
          }}
        />
      </div>
      <style>{'@keyframes eodCountdown { from { width: 100%; } to { width: 0%; } }'}</style>
      <button
        onClick={() => setShowEodModal(false)}
        style={{
          padding: '10px 20px',
          backgroundColor: '#d32f2f',
          color: '#fff',
          borderRadius: '4px',
          border: 'none',
          cursor: 'pointer',
          fontWeight: 'bold',
          fontSize: '16px'
        }}
      >
        Tamam, Kapat
      </button>
    </div>
  );
};

export default Notification;